import React, { useEffect, useCallback, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import {
  Modal,
  ModalCloseButton,
  ModalProps,
  useBreakpointValue,
} from '@thebouqs/ui';
import { DotwData, DotwModalData, DotwModalProps } from './Dotw.d';
import { dotwTrackingObjFactory } from './dotwTracking';
import useAnalytics from '@/hooks/useAnalytics';
import useTranslation from '@/hooks/useTranslation';
import { AnalyticsEvent } from '@/store/analytics/AnalyticsContext';
import DotwOriginalBox from './DotwOriginalBox';
import DotwRedesignBox from './DotwRedesignBox';

export const Dotw = ({
  open,
  data,
  autoOpen,
  setOpenDotwModal,
  display,
}: DotwModalProps): JSX.Element => {
  const wasDotwModalViewed = useRef(false);
  const [isRedesign, setIsRedesign] = useState(false);
  const { query } = useRouter() || { query: { text: '', dotw: '1' } };
  const { t } = useTranslation();
  const { track } = useAnalytics();

  useEffect(() => {
    const redesignEnabled =
      localStorage.getItem('dotw_modal_august_2024_update') === 'true';
    setIsRedesign(redesignEnabled);
  }, []);

  useEffect(() => {
    if (data && !wasDotwModalViewed.current && open) {
      const segmentPropsObj = dotwTrackingObjFactory(data);
      track(AnalyticsEvent.DEAL_OF_THE_WEEK_VIEWED, {
        ...segmentPropsObj,
      });
      wasDotwModalViewed.current = true;
    }
  }, [data, open, track]);

  useEffect(() => {
    if (!autoOpen) return;
    setOpenDotwModal(query.dotw === '1' && display);
  }, [autoOpen, query, display, setOpenDotwModal]);

  const getDotwStrings = useCallback(
    (data: DotwModalData | Record<string, never>): Record<string, string> => {
      if (data.active) {
        return {
          heading: data?.availableHeading,
          description: data?.availableDescription,
          tag: t('dotw.available.tag', { pct: data.discount }),
        };
      }
      return {
        heading: t('dotw.unavailable.heading'),
        description: t('dotw.unavailable.description'),
        tag: t('dotw.unavailable.tag', { pct: data.discount }),
      };
    },
    [t]
  );

  const dotwModalCheckoutClick = useCallback(() => {
    const segmentPropsObj = dotwTrackingObjFactory({
      ...data,
      name: 'Checkout',
    } as DotwData);
    track(AnalyticsEvent.DEAL_OF_THE_WEEK_BUTTON_CLICKED, segmentPropsObj);
  }, [data, track]);

  //Added fix used for Olapic where you added 0 based number keys to the object for useBreakpointValue to use
  const responsiveModalSize: ModalProps['size'] = useBreakpointValue({
    base: 'full',
    sm: 'full',
    md: 'md',
    lg: 'md',
    0: 'full',
    1: 'full',
    2: 'md',
    3: 'md',
  });

  if (!display) {
    return null;
  }

  const dynamicDotwStrings = getDotwStrings(data);
  const hasDiscount =
    Boolean(data?.discount) && data?.price !== data?.discountedPrice;

  return (
    <Modal
      key="dotw-modal"
      label={t('dotw.button')}
      open={open}
      size={responsiveModalSize}
      CloseButton={
        <ModalCloseButton label="close-modal" variant="normal" size="sm" />
      }
      stylesOverride={{
        dialog: {
          minHeight: isRedesign ? '27rem' : 'auto',
        },
        closeButton: {},
        body: {},
      }}
      callback={() => setOpenDotwModal(false)}>
      {isRedesign ? (
        <DotwRedesignBox
          data={data}
          dynamicDotwStrings={dynamicDotwStrings}
          hasDiscount={hasDiscount}
          onCheckoutClick={dotwModalCheckoutClick}
        />
      ) : (
        <DotwOriginalBox
          data={data}
          dynamicDotwStrings={dynamicDotwStrings}
          hasDiscount={hasDiscount}
          onCheckoutClick={dotwModalCheckoutClick}
        />
      )}
    </Modal>
  );
};

export default Dotw;
